import { GetStaticPaths, GetStaticPropsContext } from 'next'
import { useContentfulLiveUpdates } from '@contentful/live-preview/react'
import { useRouter } from 'next/router'

import { FurnitureProductProvider } from '@context/furniture-product'
import { Layout } from '@components/layouts'
import { environment } from '@config/environment'
import { getAllProductSlugs, getProduct, getPageBySlug, getHeaderAndFooter } from '@services/contentful'
import { getFurniturePrices } from '@services/prices'
import { TmFurnitureProductDetail } from 'templates'
import { loadFallback } from 'utils'
import { PgPageProps } from '../[[...slug]]'
import {
  ContentfulObjFurnitureProduct,
  ContentfulObjSeo,
  ContentType,
  ErrorFurniturePrices,
  FurniturePrices,
  SiteDomain,
} from 'types'

export type PgProductProps = PgPageProps & {
  product: ContentfulObjFurnitureProduct
  prices: FurniturePrices | ErrorFurniturePrices
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (environment.siteDomain !== SiteDomain.FURNITURE) return { paths: [], fallback: false }

  const slugs = await getAllProductSlugs()
  const normalizeSlugs = slugs.map((slug) => slug.join('/'))

  // eslint-disable-next-line
  console.log('\nGenerating static pages for the following product slugs:\n', normalizeSlugs)

  return {
    paths: normalizeSlugs.map((slug) => ({
      params: { slug },
    })),
    fallback: true,
  }
}

export const getStaticProps = async ({ params, preview }: GetStaticPropsContext<{ slug: string }>) => {
  const productSlug = params?.slug
  const isPreview = preview ?? false

  const product = (await getProduct(productSlug, preview)) as ContentfulObjFurnitureProduct
  const page = await getPageBySlug('/', isPreview)

  if (page) {
    const { footerContentful, headerContentful } = await getHeaderAndFooter(page, isPreview)

    if (product && environment.siteDomain === SiteDomain.FURNITURE) {
      const furniturePrices = await getFurniturePrices({ contentfulId: product.CONTENTFUL_ID })

      return furniturePrices
        ? {
            props: {
              ...page,
              prices: furniturePrices,
              header: headerContentful,
              product,
              footer: footerContentful,
              isPreview,
            },
            revalidate: 5,
          }
        : { notFound: true }
    }
  }

  return { notFound: true }
}

const Page = (props: PgProductProps | { notFound: boolean }) => {
  const page = props as PgProductProps
  const { isPreview, header, footer, product, prices } = page
  const updatedProduct = useContentfulLiveUpdates((props as PgProductProps).product) || product

  const router = useRouter()
  if (router.isFallback) return loadFallback()

  const productSeo: ContentfulObjSeo = {
    name: `${product.productName} SEO`,
    CONTENT_TYPE: ContentType.OBJ_SEO,
    CONTENTFUL_ID: `${product.CONTENTFUL_ID}-seo`,
    UPDATED_AT: product.UPDATED_AT,
    metaTitle: product.productName,
    metaDescription: product.metaDescription || product.productName,
    metaKeywords: product.keywords,
    ...(product.images ? { socialShareImage: product.images } : {}),
  }

  return (
    <Layout
      slug={`products/${product.productNameSlug}`}
      isPreview={isPreview}
      seo={productSeo}
      header={header}
      footer={footer}
    >
      <FurnitureProductProvider>
        <TmFurnitureProductDetail product={updatedProduct} prices={prices} />
      </FurnitureProductProvider>
    </Layout>
  )
}

export default Page
